<template>
  <div class="container-fluid goals h-100" v-if="team">
    <div class="main-container-fluid header-wrapper">
      <archived-bar class="archived-bar col-12 col-md-10 col-lg-10" v-if="team.isArchived" />
      <page-header
        :org-id="orgId"
        :team-id="teamId"
        :selected-interval="selectedInterval"
        :show-settings="isUserTeamAdmin"
        :show-new-goal-button="!team.isArchived"
        :show-inactive-timeline-info="true"
        :heading="team.name"
        @team-archived="onTeamArchiveUnarchive"
        @team-unarchived="onTeamArchiveUnarchive"
        @update:selectedInterval="changeInterval($event)"
      />
    </div>
    <div class="main-container-fluid tabs-wrapper">
      <div class="col-12 col-md-10 col-lg-10 px-0">
        <tabs
          class="navigation-tabs align-self-start"
          :items="tabs"
          :show-slider="true"
          @change="onTabChange"
          :selected-index="selectedTabIndex"
        />
      </div>
    </div>
    <vision class="mt-4" v-if="selectedTab === tabIds.vision" :org-id="orgId" :team-id="teamId" />
    <team-goals
      :org-id="orgId"
      :team-id="teamId"
      :selected-timeline="selectedInterval"
      :query="query"
      :key="teamId"
      v-else-if="selectedTab === tabIds.goals"
    />
    <div class="flex-grow-1 mh-0" v-else-if="selectedTabEntity === tabIds.initiatives">
      <div class="board-view-container h-100" v-if="initiativeTabAllowed">
        <team-initiatives-view
          :team-id="teamId"
          :org-id="orgId"
          :query="query"
          :selected-timeline="selectedInterval"
        />
      </div>
      <div class="pt-4" v-else>
        <initiatives-tab-paywall />
      </div>
    </div>
    <idea-lists v-else-if="selectedTab === tabIds.ideas" :team-id="teamId" :org-id="orgId" />
    <div class="flex-grow-1 main-container-fluid" v-else-if="selectedTab === tabIds.activity">
      <div class="h-100">
        <org-team-activity :team-id="teamId" :org-id="orgId" :query="query" />
      </div>
    </div>
  </div>
</template>

<script>
import OrgTeamActivity from '@/components/activity/OrgTeamActivity'
import ArchivedBar from '@/components/ArchivedBar'
import IdeaLists from '@/components/ideas/IdeaLists'
import PageHeader from '@/components/PageHeader'
import InitiativesTabPaywall from '@/components/paywalls/InitiativesTabPaywall'
import Tabs from '@/components/Tabs'
import TeamGoals from '@/components/TeamGoals'
import TeamInitiativesView from '@/components/TeamInitiativesView'
import Vision from '@/components/vision/Vision'
import events from '@/constants/events'
import { goalsTabIds } from '@/constants/goals'
import { USER_TYPE } from '@/constants/user'
import { defaultInterval, filterIntervals, intervalWithId, setDefaultIntervalId } from '@/interval'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import MediaQueryMixin from '@/mixins/MediaQueryMixin'
import SubOrgIdTrackerMixin from '@/mixins/SubOrgIdTrackerMixin'
import TeamMixin from '@/mixins/TeamMixin'
import cleanJSON from '@/utils/cleanJSON'
import { getTeamPageRouteConfigFromTeamInfo } from '@/utils/navigationConfigs'
import * as R from 'ramda'
import { snakeCaseToCamelCaseString } from '@/utils/stringCase'

export default {
  name: 'Team',
  components: {
    ArchivedBar,
    PageHeader,
    Tabs,
    OrgTeamActivity,
    IdeaLists,
    TeamGoals,
    Vision,
    TeamInitiativesView,
    InitiativesTabPaywall
  },
  props: ['orgId', 'teamId', 'entity', 'query'],
  mixins: [EventBusMixin, MediaQueryMixin, TeamMixin, FeaturesMixin, SubOrgIdTrackerMixin],
  beforeRouteEnter(to, from, next) {
    if (to.query.interval) {
      const timeline = to.query.interval
      const query = R.omit(['interval'], to.query)
      next({
        ...to,
        query: {
          ...query,
          timeline
        }
      })
    } else {
      next()
    }
  },
  data() {
    const tabs = this.getTabs()
    const selectedTab = tabs[this.getTabIndex(this.query.tab || this.entity)]
    return {
      filterIntervals: filterIntervals(),
      dragEnabled: false,
      tabIds: goalsTabIds,
      selectedTab: selectedTab ? selectedTab.id : goalsTabIds.goals
    }
  },
  computed: {
    isUserTeamAdmin() {
      const teamMembership = this.$store.getters.teamMemberships(this.teamId)
      const userId = this.$store.getters.user.id
      return (
        teamMembership.find(membership => membership.userId === userId)?.type === USER_TYPE.OWNER
      )
    },
    tabs() {
      return this.getTabs()
    },
    subOrgId() {
      return this.getClosestOrgLikeTeamId(this.teamId)
    },
    selectedTabIndex() {
      return this.tabs.findIndex(R.propEq('id', this.selectedTab))
    },
    selectedTabEntity() {
      return this.$route.params.entity
    },
    org() {
      return this.$store.getters.orgById(this.orgId)
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    selectedInterval() {
      if (this.query.timeline) {
        return intervalWithId(this.query.timeline)
      } else {
        return defaultInterval()
      }
    },
    initiativeTabAllowed() {
      return this.allow('initiatives_tab')
    }
  },
  methods: {
    allow(name) {
      return this.getFeatureFlagValue(this.orgId, snakeCaseToCamelCaseString('allow_' + name))
    },
    getTabs() {
      const ideasAllowed = this.allow('ideas')
      const activityTabAllowed = this.allow('activity_tab')
      const tabs = [
        { name: 'Vision', id: goalsTabIds.vision },
        { name: 'Goals', id: goalsTabIds.goals },
        { name: 'Initiatives', id: goalsTabIds.initiatives },
        ideasAllowed ? { name: 'Ideas', id: goalsTabIds.ideas } : null,
        activityTabAllowed ? { name: 'Activity', id: goalsTabIds.activity } : null
      ]
      return tabs.filter(Boolean)
    },
    getTabIndex(tabId) {
      return this.getTabs().findIndex(x => x.id == tabId)
    },
    changeInterval(interval) {
      setDefaultIntervalId(interval.id)
      this.refreshUrl({ interval }, true)
    },
    refreshUrl({ interval = {} } = {}, retainPreviousQueries) {
      let query = {
        ...(retainPreviousQueries ? this.$route.query : {}),
        timeline: interval.id || this.query.timeline
      }
      query = cleanJSON(query)
      const location = getTeamPageRouteConfigFromTeamInfo(this.team, {
        entity: this.selectedTab,
        query
      })
      this.$router.push(location)
    },
    onPageChange() {
      if (this.selectedTab !== goalsTabIds.goals) {
        this.selectedTab = goalsTabIds.goals
      }
    },
    onTabChange(tabIndex) {
      this.selectedTab = this.tabs[tabIndex].id
      this.refreshUrl()
    },
    onTeamArchiveUnarchive() {
      this.eventBusEmit(events.TEAM_ARCHIVED_UNARCHIVED)
    }
  },
  mounted() {
    if (!this.team) {
      this.$nextTick(() => this.eventBusEmit('SHOW_404'))
    }
  },
  watch: {
    orgId: 'onPageChange',
    teamId: 'onPageChange',
    $route({ params }) {
      const defaultTab = goalsTabIds.goals
      this.selectedTab = params.entity || defaultTab
      if (!params.entity) {
        this.$router.replace({
          ...this.$route,
          params: { ...this.$route.params, entity: defaultTab }
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';

.navigation-tabs {
  margin-left: 50px;
  margin-bottom: 10px;
  padding: 0 20px;
}

.board-view-container {
  display: flex;
  flex-direction: column;
}

.archived-bar {
  margin: 0 auto;
}

.goals {
  display: flex;
  flex-direction: column;
}

.tabs-wrapper,
.activity-wrapper {
  display: flex;
  justify-content: center;
  width: inherit;
  flex-shrink: 0;
}

.header-wrapper {
  padding: 0 15px;
}

.main-container-fluid {
  margin: 0 auto;
  width: 100%;
}

@include media-breakpoint-down(md) {
  .navigation-tabs {
    margin-left: 0;
    padding-left: 0;
  }
  .board-view-container {
    margin-left: 0;
  }
}
</style>
